// src/components/shared/NativeDeepLinkHandler.jsx
//
// Routes native deep links into the SPA. Today there is exactly one that
// matters: the auth-hub callback. After sign-in the hub redirects the in-app
// browser to com.divedeepdialogues.profoundphilosophy://auth/callback#token=…,
// iOS hands that URL to the app, and this handler dismisses the browser sheet,
// lifts the tokens into memory (utils/urlSecrets.js) and opens the existing
// /auth/token-exchange route — so token exchange and the post-auth redirect
// stay on one code path shared with the web build. The tokens never enter the
// webview's address, so analytics never sees them.
//
// Renders nothing; it is a listener with a component's lifecycle.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { NATIVE_AUTH_CALLBACK } from '../../services/authHubService';
import { captureUrlSecrets, clearUrlSecrets, HUB_URL_PARAMS } from '../../utils/urlSecrets';

const NativeDeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const listener = CapApp.addListener('appUrlOpen', ({ url }) => {
      if (!url) return;
      if (!url.toLowerCase().startsWith(NATIVE_AUTH_CALLBACK.toLowerCase())) return;

      // Dismiss the sheet the hub was opened in. Already-closed is not an
      // error worth surfacing.
      Browser.close().catch(() => {});

      // Read the tokens from the deep link itself — never from the webview's
      // address — replacing any left over from an earlier sign-in.
      clearUrlSecrets(HUB_URL_PARAMS);
      captureUrlSecrets(HUB_URL_PARAMS, {
        location: { href: url },
        history: { state: null, replaceState() {} },
      });
      navigate('/auth/token-exchange', { replace: true });
    });

    return () => {
      listener.then((l) => l.remove()).catch(() => {});
    };
  }, [navigate]);

  return null;
};

export default NativeDeepLinkHandler;
